import { createAuthClient } from "better-auth/react";
import {
  adminClient,
  anonymousClient,
  jwtClient,
  usernameClient,
} from "better-auth/client/plugins";

import {
  identityFrom,
  type AuthIdentity,
  type AuthUser,
  type GameplayTokenClaims,
  type LinkedAccount,
} from "./identity";

export type { AuthIdentity, AuthProvider } from "./identity";

export interface IdentityBadge {
  id: string;
  name: string;
  provider: string;
  image: string | null;
}

/**
 * Display name + provider badge per user id, the same identity the player
 * sees. Used by the admin account list; resolved server-side by the auth
 * server from the sign-in ledger.
 */
export async function fetchIdentityBadges(
  userIds: readonly string[],
): Promise<Record<string, IdentityBadge>> {
  if (userIds.length === 0) return {};
  const response = await fetch(
    `${authBaseURL}/api/identity/badges?userIds=${encodeURIComponent(userIds.join(","))}`,
    { credentials: "include" },
  );
  if (!response.ok) return {};
  const data = (await response.json()) as { badges?: IdentityBadge[] };
  return Object.fromEntries(
    (data.badges ?? []).map((badge) => [badge.id, badge]),
  );
}

export class ReauthenticationRequiredError extends Error {
  constructor() {
    super("Reauthentication is required before deleting this account");
    this.name = "ReauthenticationRequiredError";
  }
}

const authBaseURL = import.meta.env.VITE_AUTH_URL || "http://localhost:3001";

export const authClient = createAuthClient({
  baseURL: authBaseURL,
  fetchOptions: {
    credentials: "include",
  },
  plugins: [
    anonymousClient(),
    jwtClient(),
    usernameClient({ displayUsername: false }),
    adminClient(),
  ],
});

let bootstrapPromise: Promise<AuthIdentity> | null = null;
let tokenPromise: Promise<string> | null = null;
let cachedToken: { value: string; expiresAt: number } | null = null;

function clearLegacyCookies(): void {
  document.cookie = "id=; Max-Age=0; Path=/; SameSite=Lax";
  document.cookie = "user=; Max-Age=0; Path=/; SameSite=Lax";
}

function decodeTokenPayload<T>(token: string): T | null {
  try {
    return JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    ) as T;
  } catch {
    return null;
  }
}

function tokenExpiry(token: string): number {
  const payload = decodeTokenPayload<{ exp?: number }>(token);
  return typeof payload?.exp === "number" ? payload.exp * 1000 : 0;
}

function claimsFromToken(token: string): GameplayTokenClaims {
  return decodeTokenPayload<GameplayTokenClaims>(token) ?? {};
}

async function loadIdentity(): Promise<AuthIdentity> {
  clearLegacyCookies();
  let sessionResult = await authClient.getSession();
  if (!sessionResult.data) {
    const anonymousResult = await authClient.signIn.anonymous();
    if (anonymousResult.error) {
      throw new Error(anonymousResult.error.message);
    }
    sessionResult = await authClient.getSession();
  }
  if (!sessionResult.data)
    throw new Error("Authentication session unavailable");

  const accountsResult = await authClient.listAccounts();
  if (accountsResult.error) throw new Error(accountsResult.error.message);

  // The gameplay token carries the last-login-wins identity claims (name +
  // provider badge). Failure degrades to linked-provider precedence.
  let claims: GameplayTokenClaims | null = null;
  try {
    claims = claimsFromToken(await fetchGameplayToken());
  } catch {
    claims = null;
  }

  return identityFrom(
    sessionResult.data.user as AuthUser,
    (accountsResult.data || []) as LinkedAccount[],
    claims,
  );
}

export function ensureAuthIdentity(): Promise<AuthIdentity> {
  if (!bootstrapPromise) {
    bootstrapPromise = loadIdentity().catch((error) => {
      bootstrapPromise = null;
      throw error;
    });
  }
  return bootstrapPromise;
}

export function refreshAuthIdentity(): Promise<AuthIdentity> {
  bootstrapPromise = null;
  cachedToken = null;
  tokenPromise = null;
  return ensureAuthIdentity();
}

function fetchGameplayToken(): Promise<string> {
  if (cachedToken && cachedToken.expiresAt - Date.now() > 30_000) {
    return Promise.resolve(cachedToken.value);
  }
  if (tokenPromise) return tokenPromise;

  tokenPromise = authClient
    .token()
    .then(({ data, error }) => {
      if (error || !data?.token) {
        throw new Error(error?.message || "Gameplay token unavailable");
      }
      cachedToken = {
        value: data.token,
        expiresAt: tokenExpiry(data.token),
      };
      return data.token;
    })
    .finally(() => {
      tokenPromise = null;
    });
  return tokenPromise;
}

export async function getGameplayToken(): Promise<string> {
  await ensureAuthIdentity();
  return fetchGameplayToken();
}

export async function signOutToGuest(): Promise<AuthIdentity> {
  const result = await authClient.signOut();
  if (result.error) throw new Error(result.error.message);
  return refreshAuthIdentity();
}

export async function signUpWithUsername(
  username: string,
  password: string,
): Promise<AuthIdentity> {
  // Email and display name are synthesized server-side from the username;
  // the client never sends a real address.
  const result = await authClient.signUp.email({
    username,
    password,
    name: username,
    email: "",
  });
  if (result.error) {
    throw new Error(result.error.message || "Could not create the account");
  }
  return refreshAuthIdentity();
}

export async function signInWithUsername(
  username: string,
  password: string,
): Promise<AuthIdentity> {
  const result = await authClient.signIn.username({ username, password });
  if (result.error) {
    throw new Error(result.error.message || "Could not sign in");
  }
  return refreshAuthIdentity();
}

export async function isUsernameAvailable(username: string): Promise<boolean> {
  const result = await authClient.isUsernameAvailable({ username });
  if (result.error) throw new Error(result.error.message);
  return result.data?.available === true;
}

export async function deleteIdentity(
  identity: AuthIdentity,
): Promise<AuthIdentity> {
  const result = identity.isAnonymous
    ? await authClient.deleteAnonymousUser()
    : await authClient.deleteUser();
  if (result.error) {
    if (!identity.isAnonymous && result.error.code === "SESSION_EXPIRED") {
      throw new ReauthenticationRequiredError();
    }
    throw new Error(result.error.message);
  }
  return refreshAuthIdentity();
}

export async function reauthenticateForDeletion(
  identity: AuthIdentity,
): Promise<void> {
  if (identity.provider === "guest") {
    throw new Error(
      "A guest account does not have a provider to reauthenticate",
    );
  }
  if (identity.provider === "username") {
    throw new Error(
      "A username account does not need reauthentication to be deleted",
    );
  }
  const callbackURL = `${window.location.origin}/account?delete=1`;
  const result = await authClient.signIn.social({
    provider: identity.provider,
    callbackURL,
    errorCallbackURL: callbackURL,
  });
  if (result?.error) throw new Error(result.error.message);
}

export async function unlinkProvider(
  provider: "discord" | "twitch",
): Promise<AuthIdentity> {
  const accounts = await authClient.listAccounts();
  if (accounts.error) throw new Error(accounts.error.message);
  const account = accounts.data?.find(
    (candidate) => candidate.providerId === provider,
  );
  if (!account) throw new Error("Linked account not found");
  // better-auth 1.7 selects accounts by the local row id only.
  const result = await authClient.unlinkAccount({
    accountId: account.id,
  });
  if (result.error) throw new Error(result.error.message);
  return refreshAuthIdentity();
}
